import React, { createContext, useCallback, useContext } from 'react'
import {
  GoogleReCaptchaProvider,
  useGoogleReCaptcha
} from 'react-google-recaptcha-v3'

/*
 * Site key PÚBLICA do reCAPTCHA v3. Só a site key vive no cliente — a
 * RECAPTCHA_SECRET_KEY é do servidor e nunca pode ser espelhada num
 * NEXT_PUBLIC_*, porque esse prefixo faz o Next embutir o valor no bundle.
 *
 * A leitura é no topo do módulo de propósito: o webpack substitui
 * `process.env.NEXT_PUBLIC_*` por um literal em tempo de build, então o valor
 * não muda durante a vida da página e a forma da árvore React abaixo é estável.
 */
const siteKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY

/**
 * Há site key configurada? Sem ela o captcha fica desligado no cliente — em
 * desenvolvimento ninguém precisa de credencial do Google para abrir a tela de
 * login, e quem decide se o acesso vale continua sendo a API.
 */
export const isLoginCaptchaConfigured =
  typeof siteKey === 'string' && siteKey.trim() !== ''

/*
 * Contexto próprio, separado do contexto da biblioteca, só para responder "o
 * provider do Google chegou a ser montado?".
 *
 * Sem ele não dá para distinguir as duas situações: `useGoogleReCaptcha()` fora
 * de um provider devolve o contexto DEFAULT da lib, cujo `executeRecaptcha` é
 * uma função que lança — ou seja, é truthy, e um `if (!executeRecaptcha)` passa
 * reto e só descobre o problema no throw. Com esta flag o caminho sem chave nem
 * encosta naquela função.
 */
const LoginCaptchaEnabled = createContext(false)

/**
 * Envolve a tela de autenticação no provider do reCAPTCHA v3.
 *
 * Sem site key, renderiza os filhos direto: nenhum script do Google é baixado,
 * nenhum erro vai ao console e a tela não quebra.
 */
export const LoginCaptchaProvider: React.FC = ({ children }) => {
  if (!isLoginCaptchaConfigured) {
    return (
      <LoginCaptchaEnabled.Provider value={false}>
        {children}
      </LoginCaptchaEnabled.Provider>
    )
  }

  return (
    <LoginCaptchaEnabled.Provider value>
      <GoogleReCaptchaProvider
        reCaptchaKey={siteKey}
        scriptProps={{ async: true, defer: true, appendTo: 'head' }}
      >
        {children}
      </GoogleReCaptchaProvider>
    </LoginCaptchaEnabled.Provider>
  )
}

/**
 * Devolve a função que gera UM token de captcha para o submit atual.
 *
 * Precisa ser chamado de um componente FILHO de `LoginCaptchaProvider` — no
 * mesmo nível em que o provider é criado, `useGoogleReCaptcha()` leria o
 * contexto de fora e `executeRecaptcha` viria indefinido.
 *
 * O token NÃO é guardado em estado nem em ref, e isso é o ponto principal
 * daqui: token do v3 vale 2 minutos e é de uso único. Reaproveitar o anterior
 * faria o servidor recusar um login legítimo com CAPTCHA_FAILED. Cada submit
 * (entrar, token de MFA, reenvio de token, login por inatividade) chama esta
 * função de novo e gera um token novo.
 *
 * Falha ou indisponibilidade do Google devolve `undefined`: o pedido segue sem
 * token e quem decide é a API, que pode exigir o captcha (401 CAPTCHA_FAILED)
 * ou dispensá-lo. Travar o login no cliente transformaria uma instabilidade do
 * Google em indisponibilidade total do sistema.
 */
export function useLoginCaptcha(): () => Promise<string | undefined> {
  const enabled = useContext(LoginCaptchaEnabled)
  const { executeRecaptcha } = useGoogleReCaptcha()

  return useCallback(async () => {
    if (!enabled || !executeRecaptcha) {
      return undefined
    }

    try {
      return await executeRecaptcha('login')
    } catch {
      return undefined
    }
  }, [enabled, executeRecaptcha])
}
